import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { useSSRSafeId as $b5e257d569688ac6$export$619500959fc48b26 } from '../../node_modules/@react-aria/ssr/dist/import.js';
import styles from './AutoComplete.module.scss.js';
import '@livingdesign/icons';
import '@livingdesign/tokens/dist/js/light/regular/components/Form';
import '@walmart-web/livingdesign-components/index.esm.css';
import { Menu, Spinner, MenuItem } from '@walmart-web/livingdesign-components';
import '../Alert/Alert.js';
import '../Button/Button.js';
import '../Button/ButtonGroup.js';
import '../Radio/Radio.js';
import '../Tag/Tag.js';
import '../Checkbox/Checkbox.js';
import '../Chip/Chip.js';
import '../IconButton/IconButton.js';
import '../TextField/TextField.js';
import '../TabNavigation/TabNavigation.js';
import '../TabNavigation/TabNavigationItem.js';
import '../Modal/Modal.js';
import '@livingdesign/tokens/dist/js/light/regular/components/ErrorMessage';
import { SearchBox } from '../SearchBox/SearchBox.js';
import '../TextArea/TextArea.js';
import '../ProgressIndicator/ProgressIndicator.js';
import '../Panel/Panel.js';
import '../Calendar/locale/wmLocale.js';
import '../Calendar/locale/stdLocale.js';
import '../Calendar/context.js';
import 'dayjs';
import '../Calendar/RenderDays.js';
import '../Calendar/RenderWeeks.js';
import '../components/Popover/Popover.js';
import { Divider } from '../components/Divider/Divider.js';
import '../components/InfoUnderline/InfoUnderline.js';
import '../CardActionArea/CardActionArea.js';
import '../HierarchySelector/HierarchySelector.js';
import '../Pagination/Pagination.js';
import '../DisplayTag/DisplayTag.js';
import '../PageHeader/PageHeader.js';
import '../Table/Table.js';
import '../Tooltip/Tooltip.js';
import '../ValueChange/ValueChange.js';
import '../OverlayHeader/OverlayHeader.js';
import { FormLabel } from '../Form/FormLabel.js';

var _excluded = ["UNSAFE_className", "disabled", "id", "label", "placeholder", "options", "isLoading", "error", "onOptionSelected", "onEnterKeyDown", "onSearchTextChanged", "customOptionRenderer", "minInputLengthForSuggestion", "noResultsMessage", "defaultSearchValue"];
/**
 * auto complete allow users to search and the result.
 *
 * {@link https://livingdesign.walmart.com/components/text-field/react/ React documentation}
 *
 * ### Content
 *
 * #### Label
 *
 * Auto Complete requires a label to be accessible. Use the `label` property to add a label:
 *
 * ```tsx
 * <AutoComplete
 *   label="Search"
 *   onChange={(event) => console.log(event)}
 * />
 * ```
 *  ### Interaction
 *
 * when user type anything  an callback handler call the `onSearchTextChanged` prop, and also pass result in options prop.
 *
 *
 * ```tsx
 * import * as React from "react";
 *
 * export default () => {
 *   const [value, setValue] = useState("CA");
 *   const defaultOptions = [
 *  {
 *    id: '1',
 *     name: "Search term example 1",
 *   },
 *  {
 *     id: '2',
 *     name: "Activity code",
 *   },
 *   {
 *     id: '3',
 *     name: "Search term example 3",
 *   },
 *   {
 *     id: '4',
 *     name: "error",
 *   },
 *   {
 *     id: '5',
 *     name: "test",
 *   }
 * ];
 *
 *   return (
 *     <AutoComplete
 *       label="Full name"
 *       onSearchTextChanged={(event) => setValue(event.target.value)}
 *       options={defaultOptions}
 *     />
 *   );
 * };
 * ```
 */

var AutoComplete = function AutoComplete(props) {
  var UNSAFE_className = props.UNSAFE_className,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    initialId = props.id,
    label = props.label,
    placeholder = props.placeholder,
    options = props.options,
    isLoading = props.isLoading,
    error = props.error,
    onOptionSelected = props.onOptionSelected,
    onEnterKeyDown = props.onEnterKeyDown,
    onSearchTextChanged = props.onSearchTextChanged,
    customOptionRenderer = props.customOptionRenderer,
    minInputLengthForSuggestion = props.minInputLengthForSuggestion,
    noResultsMessage = props.noResultsMessage,
    defaultSearchValue = props.defaultSearchValue,
    rest = _objectWithoutProperties(props, _excluded);
  var id = $b5e257d569688ac6$export$619500959fc48b26(initialId);
  var searchBoxref = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    searchValue = _useState4[0],
    setSearchValue = _useState4[1];
  var scrollDivref = useRef(null);
  var scrollMenuPositionOnTop = function scrollMenuPositionOnTop() {
    if (scrollDivref.current) {
      scrollDivref.current.scrollTop = 0;
    }
  };
  var onChangeSearchBox = function onChangeSearchBox(value) {
    setSearchValue(value);
    handleSearchTextValueChanged(value);
  };
  var handleSearchTextValueChanged = function handleSearchTextValueChanged(value) {
    onSearchTextChanged(value);
    if (value.length > minInputLengthForSuggestion) {
      setIsOpen(true);
      scrollMenuPositionOnTop();
    } else {
      setIsOpen(false);
    }
  };
  var onSearchBoxKeyDown = function onSearchBoxKeyDown(e) {
    if (e.key === 'Enter') {
      setIsOpen(false);
      onEnterKeyDown();
    }
  };
  var clearSearchValue = function clearSearchValue() {
    setSearchValue('');
    onSearchTextChanged('');
  };
  var handleOptionClick = function handleOptionClick(option, index) {
    setSearchValue(option === null || option === void 0 ? void 0 : option.name);
    onOptionSelected(option, index);
    setIsOpen(false);
  };
  useEffect(function () {
    if (defaultSearchValue) {
      setSearchValue(defaultSearchValue);
    }
  }, [defaultSearchValue]);
  var getBoldFilteredText = function getBoldFilteredText(text, term) {
    var searchTermRegex = new RegExp("(".concat(searchValue.replace(/[.\\+*?[^\]$(){}=!<>|:-]/g, '\\$&'), ")"), 'gi');
    var substrings = text.split(searchTermRegex);
    return /*#__PURE__*/React__default.createElement("div", null, substrings.map(function (substring, index) {
      return substring.toLowerCase() === term.toLowerCase() ? /*#__PURE__*/React__default.createElement("strong", {
        key: index
      }, substring) : substring;
    }));
  };
  var showNoResultsFound = function showNoResultsFound() {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(MenuItem, {
      UNSAFE_className: classNames(styles.menuForNoResult)
    }, /*#__PURE__*/React__default.createElement("div", null, /*#__PURE__*/React__default.createElement("span", null, /*#__PURE__*/React__default.createElement("b", null, noResultsMessage !== null && noResultsMessage !== void 0 && noResultsMessage.title ? noResultsMessage === null || noResultsMessage === void 0 ? void 0 : noResultsMessage.title : 'No results found')), /*#__PURE__*/React__default.createElement("p", {
      className: styles.noResultsDescription
    }, noResultsMessage !== null && noResultsMessage !== void 0 && noResultsMessage.description ? noResultsMessage === null || noResultsMessage === void 0 ? void 0 : noResultsMessage.description : 'Try adjusting your search options to find what you’re looking for'))));
  };
  var showMenuListOptions = function showMenuListOptions() {
    if (options && options.length > 0) {
      var totalOption = options.length;
      return options.map(function (item, index) {
        return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(MenuItem, {
          key: index,
          UNSAFE_className: styles.menuItem,
          onClick: function onClick() {
            return handleOptionClick(item, index);
          }
        }, customOptionRenderer ? customOptionRenderer(item) : getBoldFilteredText(item.name, searchValue)), index < totalOption - 1 && /*#__PURE__*/React__default.createElement(Divider, null));
      });
    }
    return showNoResultsFound();
  };
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", _extends({
    className: classNames(styles.root, disabled && styles.disabled, UNSAFE_className)
  }, rest), label ? /*#__PURE__*/React__default.createElement(FormLabel, {
    className: styles.label,
    disabled: disabled,
    htmlFor: id
  }, label) : null, /*#__PURE__*/React__default.createElement("div", {
    className: classNames(styles.menuContainer)
  }, /*#__PURE__*/React__default.createElement(Menu, {
    isOpen: isOpen,
    onClose: function onClose() {
      return setIsOpen(false);
    },
    onOpen: function onOpen() {
      return setIsOpen(true);
    },
    position: "bottomLeft",
    trigger: /*#__PURE__*/React__default.createElement(SearchBox, {
      ref: searchBoxref,
      label: '',
      onChange: function onChange(e) {
        return onChangeSearchBox(e.target.value);
      },
      onKeyDown: function onKeyDown(e) {
        return onSearchBoxKeyDown(e);
      },
      value: searchValue,
      placeholder: placeholder,
      error: error,
      onCloseIconClick: clearSearchValue
    }),
    triggerRef: searchBoxref
  }, isLoading ? /*#__PURE__*/React__default.createElement("div", {
    className: classNames(styles.loaderWrapper)
  }, /*#__PURE__*/React__default.createElement(Spinner, {
    color: "gray",
    size: "large"
  })) : /*#__PURE__*/React__default.createElement("div", {
    className: styles.menuItemMaxHeight,
    id: "scrollableDiv",
    ref: scrollDivref
  }, options && options.length > 0 ? showMenuListOptions() : showNoResultsFound())))));
};
AutoComplete.displayName = 'AutoComplete';

export { AutoComplete };
